import React, { ReactNode, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, Link as RouterLink, useLocation } from 'react-router-dom';
import { Box, Button, Container, Flex, HStack, Spacer, Text } from '@chakra-ui/react';
import {
  CloseIcon,
  CpuIcon,
  GridIcon,
  LayersIcon,
  LogOutIcon,
  MenuIcon,
  UploadCloudIcon,
  UsersIcon,
  InventoryIcon,
  ShoppingCartIcon,
} from './components/Icons';
import LoginPage from './components/LoginPage';
import Dashboard from './components/Dashboard';
import CompaniesPage from './pages/CompaniesPage';
import BranchesPage from './pages/BranchesPage';
import SystemsPage from './pages/SystemsPage';
import SystemStatusUploadPage from './pages/SystemStatusUploadPage';
import SoftwareVersionsPage from './pages/SoftwareVersionsPage';
import ResellersPage from './pages/ResellersPage';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider, ConfirmationProvider } from './context/NotificationContext';

const ProtectedRoute: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { token } = useAuth();
  return token ? <>{children}</> : <Navigate to="/login" replace />;
};

type NavLink = {
  label: string;
  to: string;
  icon: React.FC<{ size?: number | string }>;
};

const navLinks: NavLink[] = [
  { label: 'Dashboard', to: '/dashboard', icon: GridIcon },
  { label: 'Companies', to: '/companies', icon: UsersIcon },
  { label: 'Branches', to: '/branches', icon: LayersIcon },
  { label: 'Resellers', to: '/resellers', icon: ShoppingCartIcon },
  { label: 'Systems', to: '/systems', icon: CpuIcon },
  { label: 'Software Versions', to: '/software-versions', icon: InventoryIcon },
  { label: 'Status Upload', to: '/system-status-upload', icon: UploadCloudIcon },
];

const NavMenu = () => {
  const { logout } = useAuth();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const isActivePath = (to: string) => {
    const path = location.pathname;
    return path === to || path.startsWith(to + '/');
  };

  const dashboardLink = navLinks[0]; // Dashboard is always visible
  const otherLinks = navLinks.slice(1); // Rest go in mobile menu

  return (
    <Box bg="white" shadow="sm" borderBottom="1px" borderColor="gray.100" position="relative">
      <Container maxW="100%" px={{ base: 2, md: 3 }}>
        <Flex align="center" py={2} gap={2}>
          <RouterLink to="/dashboard" style={{ textDecoration: 'none' }}>
            <Text fontWeight="bold" fontSize={{ base: 'md', md: 'lg' }} color="brand.600" whiteSpace="nowrap" _hover={{ color: 'brand.700' }} cursor="pointer">
              Key Management
            </Text>
          </RouterLink>
          <Spacer />
          
          {/* Dashboard - Always visible */}
          <RouterLink to={dashboardLink.to} style={{ textDecoration: 'none' }}>
            <Button
              variant={isActivePath(dashboardLink.to) ? 'solid' : 'ghost'}
              colorPalette={isActivePath(dashboardLink.to) ? 'brand' : undefined}
              size="sm"
              gap={1}
              display="inline-flex"
              alignItems="center"
            >
              <dashboardLink.icon size={16} />
              <Text display={{ base: 'none', sm: 'inline' }}>{dashboardLink.label}</Text>
            </Button>
          </RouterLink>

          {/* Desktop Nav - Hidden on mobile */}
          <HStack gap={1} display={{ base: 'none', lg: 'flex' }}>
            {otherLinks.map(({ label, to, icon: IconComponent }) => {
              const isActive = isActivePath(to);
              return (
                <RouterLink key={to} to={to} style={{ textDecoration: 'none' }}>
                  <Button
                    variant={isActive ? 'solid' : 'ghost'}
                    colorPalette={isActive ? 'brand' : undefined}
                    size="sm"
                    gap={1}
                    display="inline-flex"
                    alignItems="center"
                  >
                    <IconComponent size={16} />
                    {label}
                  </Button>
                </RouterLink>
              );
            })}
          </HStack>

          {/* Logout - Always visible on desktop */}
          <Button
            colorPalette="red"
            variant="outline"
            size="sm"
            onClick={logout}
            display={{ base: 'none', lg: 'inline-flex' }}
            alignItems="center"
            gap={1}
          >
            <LogOutIcon size={16} />
            Logout
          </Button>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="sm"
            display={{ base: 'inline-flex', lg: 'none' }}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <CloseIcon size={20} /> : <MenuIcon size={20} />}
          </Button>
        </Flex>
      </Container>

      {/* Mobile Menu Dropdown */}
      {isMenuOpen && (
        <Box
          display={{ base: 'block', lg: 'none' }}
          position="absolute"
          top="100%"
          left={0}
          right={0}
          bg="white"
          shadow="md"
          borderTop="1px"
          borderColor="gray.100"
          zIndex={100}
          py={1}
        >
          <Container maxW="100%" px={{ base: 2, md: 3 }}>
            <Flex direction="column" gap={1}>
              {otherLinks.map(({ label, to, icon: IconComponent }) => {
                const isActive = isActivePath(to);
                return (
                  <RouterLink key={to} to={to} style={{ textDecoration: 'none' }} onClick={() => setIsMenuOpen(false)}>
                    <Button
                      variant={isActive ? 'solid' : 'ghost'}
                      colorPalette={isActive ? 'brand' : undefined}
                      size="sm"
                      gap={2}
                      display="flex"
                      alignItems="center"
                      justifyContent="flex-start"
                      w="100%"
                    >
                      <IconComponent size={16} />
                      {label}
                    </Button>
                  </RouterLink>
                );
              })}
              <Button
                colorPalette="red"
                variant="outline"
                size="sm"
                onClick={() => { logout(); setIsMenuOpen(false); }}
                display="flex"
                alignItems="center"
                justifyContent="flex-start"
                gap={2}
                w="100%"
                mt={2}
              >
                <LogOutIcon size={16} />
                Logout
              </Button>
            </Flex>
          </Container>
        </Box>
      )}
    </Box>
  );
};

const AppShell = () => (
  <Flex direction="column" minH="100vh" bg="gray.50" overflow="hidden">
    <NavMenu />
    <Box flex="1" py={{ base: 2, md: 4 }} overflow="hidden">
      <Container maxW="100%" px={{ base: 2, md: 3 }} overflow="hidden">
        <Outlet />
      </Container>
    </Box>
  </Flex>
);

function App() {
  return (
    <NotificationProvider>
      <ConfirmationProvider>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/" element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="companies" element={<CompaniesPage />} />
                <Route path="branches" element={<BranchesPage />} />
                <Route path="resellers" element={<ResellersPage />} />
                <Route path="systems" element={<SystemsPage />} />
                <Route path="software-versions" element={<SoftwareVersionsPage />} />
                <Route path="system-status" element={<Navigate to="/systems" replace />} />
                <Route path="system-status-upload" element={<SystemStatusUploadPage />} />
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="*" element={<Navigate to="dashboard" replace />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </ConfirmationProvider>
    </NotificationProvider>
  );
}

export default App;
